import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Gavi } from '#components/mobile/travesia/Gavi';
import { color, texto } from '#components/mobile/ui/tokens';

import { Enlace } from './PantallaCofre';
import { CIFRAS_PIN, guardarPin } from './pin';
import { PuntosPin, TecladoPin } from './TecladoPin';

/**
 * Elegir el PIN de 4 cifras, escrito dos veces. Sale al encender «Pedir la
 * huella al abrir» y en «Cambiar el PIN».
 */
export function CrearPin({
  onHecho,
  onCancelar,
}: {
  onHecho: () => void;
  onCancelar: () => void;
}) {
  const { t } = useTranslation();
  const [primero, setPrimero] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [distintos, setDistintos] = useState(0);

  async function escribirCifra(cifra: string) {
    if (pin.length >= CIFRAS_PIN) {
      return;
    }
    const nuevo = pin + cifra;
    setPin(nuevo);
    if (nuevo.length < CIFRAS_PIN) {
      return;
    }
    if (primero === null) {
      setPrimero(nuevo);
      setPin('');
      return;
    }
    if (nuevo !== primero) {
      setPrimero(null);
      setPin('');
      setDistintos(d => d + 1);
      return;
    }
    await guardarPin(nuevo);
    onHecho();
  }

  return createPortal(
    <View
      data-testid="crear-pin"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        backgroundColor: color.bg,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        overflowY: 'auto',
      }}
    >
      <View
        style={{ width: '100%', maxWidth: 360, alignItems: 'center', gap: 22 }}
      >
        <Gavi
          estado={distintos > 0 && primero === null ? 'eriza' : 'reposo'}
          clave={distintos}
          ancho={72}
        />
        <View style={{ gap: 6, alignItems: 'center' }}>
          <h1
            style={{
              ...texto.display,
              margin: 0,
              color: color.fg,
              textAlign: 'center',
            }}
          >
            {primero === null ? (
              <Trans>Choose a 4-digit PIN</Trans>
            ) : (
              <Trans>Type it again</Trans>
            )}
          </h1>
          <Text
            style={{
              ...texto.cuerpo,
              fontSize: 15,
              color: distintos > 0 && primero === null ? color.bad : color.fg2,
              textAlign: 'center',
            }}
          >
            {distintos > 0 && primero === null
              ? t("They don't match. Let's start again.")
              : t(
                  'If your fingerprint fails, you open Rumbo with it. It only works on this phone.',
                )}
          </Text>
        </View>
        <View key={distintos}>
          <PuntosPin escritas={pin.length} error={distintos > 0 && !pin} />
        </View>
        <TecladoPin
          onCifra={c => void escribirCifra(c)}
          onBorrar={() => setPin(p => p.slice(0, -1))}
        />
        <View style={{ alignSelf: 'center' }}>
          <Enlace onPress={onCancelar}>
            <Trans>Cancel</Trans>
          </Enlace>
        </View>
      </View>
    </View>,
    document.body,
  );
}
